import { ArrowUpRight, ShoppingBag } from 'lucide-react'
import { useEffect } from 'react'

import { DiscordIcon } from '@/site/ui'

import { DISCORD, SITE, STATUS } from './api'
import { BasketDrawer, Notice, SignInDialog } from './Basket'
import { CategoryNav } from './Nav'
import { CategoryPage, ClosedPage, HomePage, NotFoundPage, PackagePage, ThanksPage } from './Pages'
import { useRoute } from './router'
import { useBasketCount, useStore } from './state'
import { Link, Spinner } from './ui'

function Header() {
  const count = useBasketCount()
  const open = useStore((state) => state.token !== null && state.token !== undefined)
  const setDrawer = useStore((state) => state.setDrawer)
  const username = useStore((state) => state.basket?.username)

  return (
    <header className="sticky top-0 z-40 -mx-4 mb-2 px-4 pt-3 sm:-mx-6 sm:px-6">
      <div className="glass rounded-[26px] py-2 pr-2 pl-4 shadow-[0_16px_50px_-24px_rgb(0_0_0/0.9)] sm:pl-5 lg:rounded-full">
        <div className="flex items-center justify-between gap-3">
          <Link to="/" className="flex shrink-0 items-center gap-3" aria-label="Unity RP, store">
            <img
              src="/brand/unity-lockup.png"
              alt=""
              width={858}
              height={521}
              className="h-7 w-auto shrink-0 select-none sm:h-8"
              draggable={false}
            />
            <span className="h-5 w-px bg-white/15" aria-hidden />
            <span className="font-display text-fog text-[13px] font-medium tracking-[0.02em]">
              Store
            </span>
          </Link>
          <CategoryNav className="hidden min-w-0 flex-1 lg:block" />
          <nav className="flex shrink-0 items-center gap-1.5" aria-label="Collegamenti">
            <a
              href={SITE}
              className="text-mist hidden items-center gap-1 rounded-full px-3 py-1.5 text-[13px] font-medium hover:bg-white/5 hover:text-white md:inline-flex"
            >
              unityrp.it
              <ArrowUpRight className="size-3.5" aria-hidden />
            </a>
            <a
              href={DISCORD}
              className="text-mist hidden size-9 place-items-center rounded-full hover:bg-white/5 hover:text-white sm:grid"
              aria-label="Discord"
            >
              <DiscordIcon className="size-[18px]" />
            </a>
            {username && (
              <span className="text-mist hidden max-w-[10rem] truncate px-2 text-[13px] lg:inline">
                {username}
              </span>
            )}
            {open && (
              <button
                type="button"
                onClick={() => setDrawer(true)}
                className="btn-primary relative inline-flex h-10 items-center gap-2 rounded-full px-4 text-[13px] font-semibold"
                aria-label={`Carrello, ${count} ${count === 1 ? 'articolo' : 'articoli'}`}
              >
                <ShoppingBag className="size-4" aria-hidden />
                <span className="hidden sm:inline">Carrello</span>
                {count > 0 && (
                  <span
                    key={count}
                    className="animate-bump text-blue grid h-5 min-w-5 place-items-center rounded-full bg-white px-1 text-[11px] font-bold tabular-nums"
                  >
                    {count}
                  </span>
                )}
              </button>
            )}
          </nav>
        </div>
        <CategoryNav className="-ml-1 border-t border-white/[0.06] pt-1.5 mt-2 lg:hidden" />
      </div>
    </header>
  )
}

function Footer() {
  return (
    <footer className="text-mist mt-24 border-t border-white/[0.06] py-8 text-[12.5px] leading-relaxed">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p>
          © {new Date().getFullYear()} Unity RP ·{' '}
          <a href={SITE} className="hover:text-white">
            unityrp.it
          </a>{' '}
          ·{' '}
          <a href={STATUS} className="hover:text-white">
            Stato dei servizi
          </a>{' '}
          ·{' '}
          <a href={DISCORD} className="hover:text-white">
            Supporto su Discord
          </a>
        </p>
        <p>Pagamenti gestiti da Tebex</p>
      </div>
      <p className="mt-4 max-w-3xl text-[11.5px] opacity-80">
        Unity RP non è affiliato a Rockstar Games, Take-Two Interactive o Cfx.re.
      </p>
    </footer>
  )
}

/**
 * store.unityrp.it: the city's shop on Tebex's Headless API, in the community site's
 * colours. The catalogue and the basket are Tebex's; the page is ours — docs/STORE.md.
 */
export function StoreApp() {
  const route = useRoute()
  const token = useStore((state) => state.token)
  const failed = useStore((state) => state.failed)
  const boot = useStore((state) => state.boot)

  useEffect(() => {
    void boot()
  }, [boot])

  return (
    <div className="sky min-h-dvh overflow-x-clip">
      <div className="mx-auto w-full max-w-[1120px] px-4 sm:px-6">
        <Header />
        <main className="min-h-[60vh]">
          {token === undefined ? (
            <div className="text-mist grid place-items-center py-32">
              <Spinner className="size-6" />
            </div>
          ) : token === null ? (
            <ClosedPage failed={failed} />
          ) : (
            <>
              {route.name === 'home' && <HomePage />}
              {route.name === 'category' && <CategoryPage key={route.id} id={route.id} />}
              {route.name === 'package' && <PackagePage key={route.id} id={route.id} />}
              {route.name === 'thanks' && <ThanksPage />}
              {route.name === 'missing' && <NotFoundPage />}
            </>
          )}
        </main>
        <Footer />
      </div>
      <BasketDrawer />
      <SignInDialog />
      <Notice />
    </div>
  )
}
